import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Download, UserRound } from "lucide-react";

import {
    DateRangeFilter,
    FILTER_LABEL,
    HorizontalBarChart,
    ReportChartCard,
    formatCount,
    useChartTokens,
    usePagedRows,
} from "../../../../../shared/components/reports";
import { Pagination } from "../../../../../shared/ui/common/Pagination";
import FilterSelect from "../../../../../shared/ui/form/FilterSelect";
import Label from "../../../../../shared/ui/form/Label";
import { useCenterMembers, useCenterReports } from "../../../../center";
import {
    BillableSessionsTable,
    MentorTable,
    MonthStatementDialog,
    reportLinks,
    usePractitionerCourses,
    usePractitionerReport,
} from "../../../../reports";
import { useTherapist } from "../../../../../context/useTherapist";
import Button from "../../../../../shared/ui/button/Button";

/** Rows in alphabetical order of their professional's name. */
function sortByName<T extends { therapist_id: string }>(
    rows: T[],
    members: { therapist_id: string; first_name: string; last_name: string }[],
    former: string
): T[] {
    const names = new Map(members.map((m) => [m.therapist_id, `${m.first_name} ${m.last_name}`]));
    const name = (id: string) => names.get(id) ?? former;
    return [...rows].sort((a, b) => name(a.therapist_id).localeCompare(name(b.therapist_id)));
}

/** More bars than this and the chart stops being read; the table keeps everybody. */
export const MAX_CHART_BARS = 10;

interface Props {
    centerId: string;
    from: string;
    to: string;
    therapistId: string;
    onFromChange: (day: string) => void;
    onToChange: (day: string) => void;
    onTherapistChange: (id: string) => void;
    onClear: () => void;
}

/**
 * How the work is shared among the centre's professionals: who held most
 * sessions, and each one's sessions and bookings — with the way into each
 * one's own report.
 */
export default function CenterTherapistReports({
    centerId,
    from,
    to,
    therapistId,
    onFromChange,
    onToChange,
    onTherapistChange,
    onClear,
}: Props) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const tokens = useChartTokens();
    const count = (value: number) => formatCount(value, locale);
    const { members } = useCenterMembers(centerId);
    const { therapist } = useTherapist();
    const [downloadOpen, setDownloadOpen] = useState(false);
    const range = { start: `${from}T00:00:00`, end: `${to}T23:59:59` };
    // Bookings still come from the agenda report; everything about sessions
    // comes from the report across professionals, which prices each one at
    // its own professional's rates and matches the downloaded statement.
    const { agenda, loading: agendaLoading } = useCenterReports(centerId, range, therapistId || undefined);
    const centreScope = useMemo(() => ({ kind: "centre" as const, centerId }), [centerId]);
    const centreFilters = useMemo(
        () => ({ from, to, granularity: "month" as const, subject: "", course: "" }),
        [from, to]
    );
    const { report, loading, error, retry } = usePractitionerReport(centreScope, centreFilters);
    const { courses } = usePractitionerCourses(centreScope, from, to);
    const links = useMemo(() => reportLinks(centreScope, therapist?.id), [centreScope, therapist?.id]);

    // Somebody who has left the centre keeps the work they did here, but is
    // no longer on the roster to be named from.
    const therapistName = (id: string) => {
        const member = members.find((m) => m.therapist_id === id);
        return member ? `${member.first_name} ${member.last_name}` : t("center.reports.formerMember");
    };
    const therapistOptions = members
        .filter((m) => m.is_practising_therapist)
        .map((m) => ({ value: m.therapist_id, label: `${m.first_name} ${m.last_name}` }));

    const mentors = (report?.mentors ?? []).filter((row) => !therapistId || row.therapist_id === therapistId);
    const load = [...mentors].filter((row) => row.sessions > 0).sort((a, b) => b.sessions - a.sessions);
    const charted = load.slice(0, MAX_CHART_BARS);
    const former = t("center.reports.formerMember");
    const agendaRows = useMemo(() => sortByName(agenda, members, former), [agenda, members, former]);
    const pagedAgenda = usePagedRows(agendaRows);

    const heading = "py-2 pr-4 font-medium";
    const cell = "py-3 pr-4";

    return (
        <>
            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="mb-4 flex justify-end">
                    <Button
                        size="sm"
                        variant="outline"
                        startIcon={<Download className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => setDownloadOpen(true)}
                    >
                        {t("reports.download.button")}
                    </Button>
                </div>
                <DateRangeFilter
                    idPrefix="therapist-reports"
                    from={from}
                    to={to}
                    onFromChange={onFromChange}
                    onToChange={onToChange}
                    onClear={onClear}
                >
                    <div className="space-y-1.5">
                        <Label className={FILTER_LABEL}>{t("center.agenda.therapist")}</Label>
                        <FilterSelect
                            options={therapistOptions}
                            value={therapistId}
                            onChange={(value) => onTherapistChange(value as string)}
                            placeholder={t("center.agenda.everyone")}
                            searchPlaceholder={t("center.patients.searchTherapist")}
                            icon={<UserRound className="h-5 w-5" />}
                            className="text-sm"
                        />
                    </div>
                </DateRangeFilter>
                {error && (
                    <div role="alert" className="flex items-center justify-between gap-3 text-sm text-status-critical">
                        <span>{t("reports.error")}</span>
                        <Button size="sm" variant="outline" onClick={retry}>
                            {t("reports.retry")}
                        </Button>
                    </div>
                )}
            </section>

            <ReportChartCard
                title={t("center.reports.therapistsTitle")}
                description={
                    load.length > MAX_CHART_BARS
                        ? t("center.reports.therapistsTopHint", { count: MAX_CHART_BARS })
                        : t("center.reports.therapistsHint")
                }
                empty={!loading && load.length === 0}
            >
                {loading ? (
                    <p className="text-sm text-content-secondary">{t("common.loading")}</p>
                ) : (
                    <HorizontalBarChart
                        names={charted.map((row) => row.name || therapistName(row.therapist_id))}
                        values={charted.map((row) => row.sessions)}
                        tokens={tokens}
                        seriesName={t("center.reports.sessions")}
                        formatValue={count}
                        ariaLabel={t("center.reports.therapistsTitle")}
                    />
                )}
            </ReportChartCard>

            {report && <MentorTable report={report} locale={locale} links={links} only={therapistId || undefined} />}

            {report && (
                <BillableSessionsTable report={report} locale={locale} links={links} only={therapistId || undefined} />
            )}

            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                {agendaLoading && <p className="text-sm text-content-secondary">{t("common.loading")}</p>}
                {!agendaLoading && (
                    <div className="flex flex-col gap-8">
                        <div>
                            <h3 className="mb-2 text-sm font-medium text-content-primary">
                                {t("center.reports.agenda")}
                            </h3>
                            {agenda.length === 0 ? (
                                <p className="text-sm text-content-secondary">{t("center.reports.empty")}</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[480px] text-left text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-content-tertiary dark:border-gray-800">
                                                <th scope="col" className={heading}>{t("center.reports.therapist")}</th>
                                                <th scope="col" className={heading}>{t("center.agenda.states.scheduled")}</th>
                                                <th scope="col" className={heading}>{t("center.agenda.states.completed")}</th>
                                                <th scope="col" className={heading}>{t("center.agenda.states.cancelled")}</th>
                                                <th scope="col" className={heading}>{t("center.reports.bookedByPatient")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pagedAgenda.page.map((row) => (
                                                <tr
                                                    key={row.therapist_id}
                                                    className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                                                >
                                                    <th scope="row" className={`${cell} font-medium text-content-primary`}>
                                                        <Link
                                                            to={`/center/reports/practitioners/${row.therapist_id}`}
                                                            className="font-medium text-brand-600 underline-offset-2 hover:underline dark:text-brand-300"
                                                        >
                                                            {therapistName(row.therapist_id)}
                                                        </Link>
                                                    </th>
                                                    <td className={cell}>{row.scheduled}</td>
                                                    <td className={cell}>{row.completed}</td>
                                                    <td className={cell}>{row.cancelled}</td>
                                                    <td className={cell}>{row.booked_by_patient}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <Pagination
                                offset={pagedAgenda.offset}
                                total={pagedAgenda.total}
                                pageSize={pagedAgenda.pageSize}
                                onPrev={pagedAgenda.onPrev}
                                onNext={pagedAgenda.onNext}
                            />
                        </div>
                    </div>
                )}
            </section>

            <MonthStatementDialog
                isOpen={downloadOpen}
                onClose={() => setDownloadOpen(false)}
                scope={centreScope}
                initialMonth={to.slice(0, 7)}
                subjects={[]}
                initialSubject=""
                courses={courses}
                therapist={therapistId ? { id: therapistId, name: therapistName(therapistId) } : undefined}
            />
        </>
    );
}
